import {isPlatformBrowser} from "@angular/common";
import {inject, PLATFORM_ID, REQUEST, Service, type Signal, signal} from "@angular/core";
import type {Nullable} from "../helpers/TypeHelpers";

export type Theme = "light" | "dark";
export type ThemeMode = "auto" | Theme;

function isTheme(value: unknown): value is Theme {
  return typeof value === "string" && (value === "light" || value === "dark");
}

function readCurrentThemeMode(platformId: object, request: Nullable<Request>): ThemeMode {
  if (isPlatformBrowser(platformId)) {
    const localStorageThemeMode = localStorage.getItem("rfTheme");
    if (isTheme(localStorageThemeMode)) return localStorageThemeMode;
  } else if (request) {
    const clientHintThemeMode = request.headers.get("sec-ch-prefers-color-scheme");
    if (isTheme(clientHintThemeMode)) return clientHintThemeMode;
  }
  return "auto";
}

@Service()
export class ThemeStorage {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly _themeMode = signal(readCurrentThemeMode(this.platformId, inject(REQUEST)));

  public get themeMode(): Signal<ThemeMode> {
    return this._themeMode.asReadonly();
  }

  public setThemeMode(mode: ThemeMode): void {
    this._themeMode.set(mode);
    if (isPlatformBrowser(this.platformId)) {
      if (mode === "auto")
        localStorage.removeItem("rfTheme");
      else
        localStorage.setItem("rfTheme", mode);
    }
  }
}
